import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Mail, 
  Phone, 
  MapPin,
  Send,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [role, setRole] = useState("");
  const [partners, setPartners] = useState("");
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    
    const contactData = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      role: role || undefined,
      practice: formData.get("practice") as string || undefined,
      partners: partners || undefined,
      location: formData.get("location") as string || undefined,
      message: formData.get("message") as string,
    };

    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: contactData,
      });

      if (error || !data?.success) {
        throw new Error(data?.error || error?.message || "Failed to send message");
      }

      setSubmitted(true);
      toast({
        title: "Message sent",
        description: "Thank you for getting in touch. We'll respond within 1-2 working days.",
      });
    } catch (error) {
      console.error("Contact form error:", error);
      toast({
        title: "Error sending message",
        description: "Please try again later or email us directly at hello@partnershiplab.co.uk",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="section-padding bg-primary-light">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">
              Get in Touch
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Whether you're ready to start or just exploring options, we'd love to hear from you. 
              Book a discovery call or send us a message.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="card-premium">
                <h2 className="font-display text-2xl font-semibold text-foreground mb-6">
                  Send us a message
                </h2>

                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
                    <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                      Message received
                    </h3>
                    <p className="text-muted-foreground">
                      Thank you for getting in touch. We'll respond within 1-2 working days.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                          Your name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          required
                          placeholder="Dr Jane Smith"
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                          Email address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="jane.smith@practice.nhs.uk"
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
                          Your role
                        </label>
                        <Select value={role} onValueChange={setRole}>
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Select your role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gp-partner">GP Partner</SelectItem>
                            <SelectItem value="salaried-gp">Salaried GP</SelectItem>
                            <SelectItem value="practice-manager">Practice Manager</SelectItem>
                            <SelectItem value="pcn-leader">PCN Leader</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label htmlFor="practice" className="block text-sm font-medium text-foreground mb-2">
                          Practice name
                        </label>
                        <Input
                          id="practice"
                          name="practice"
                          placeholder="Your practice name"
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="partners" className="block text-sm font-medium text-foreground mb-2">
                          Number of partners
                        </label>
                        <Select value={partners} onValueChange={setPartners}>
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2-3">2-3 partners</SelectItem>
                            <SelectItem value="4-6">4-6 partners</SelectItem>
                            <SelectItem value="7-10">7-10 partners</SelectItem>
                            <SelectItem value="10+">10+ partners</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-foreground mb-2">
                          Location
                        </label>
                        <Input
                          id="location"
                          name="location"
                          placeholder="City or region"
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                        Your message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        placeholder="Tell us about your practice and what you're looking to achieve..."
                        className="rounded-xl resize-none"
                      />
                    </div>

                    <Button type="submit" variant="hero" size="lg" className="w-full sm:w-auto" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="card-premium">
                <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                  Book a discovery call
                </h3>
                <p className="text-muted-foreground mb-4">
                  A free, no-obligation 20-minute call to discuss your practice's needs 
                  and explore how we might help.
                </p>
                <Button variant="hero" className="w-full">
                  Schedule a Call
                </Button>
              </div>

              <div className="card-premium">
                <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                  Contact details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <span className="text-muted-foreground">
                      hello@partnershiplab.co.uk
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <span className="text-muted-foreground">
                      Available by appointment
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">
                      Based in London, serving practices across the UK
                    </span>
                  </div>
                </div>
              </div>

              <div className="card-premium bg-primary-light border-none">
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  Response time
                </h3>
                <p className="text-muted-foreground">
                  We aim to respond to all enquiries within 1-2 working days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
